/**
 * remoteAudio — per-remote-user listening chain (the consumer side of the
 * vocal chain).
 *
 * For each watched user, their consumed audio track is routed through a
 * Web Audio graph and the PROCESSED track is spliced back into the stream the
 * tile's <video> element plays:
 *
 *   raw remote audio ──→ hidden <audio muted>   (keep-alive: a remote WebRTC
 *          │              track must be attached to a media element or some
 *          │              browsers stop delivering it into Web Audio)
 *          └─→ Source → gain(0..4) → compressor → limiter → analyser
 *                                                   → MediaStreamDestination
 *   playback stream = [remote video track, processed audio track]
 *
 * Because the ELEMENT plays the processed track (rather than us muting it and
 * playing via ctx.destination), the native <video controls> stay fully
 * functional — play/pause, fullscreen, AirPlay/Chromecast carry the processed
 * audio, and the native volume slider still works as a final 0-100%
 * attenuator on top of our 0-400% boost.
 *
 * Keyed by userId and owned at App level: tiles unmount/remount when popped
 * out into windows, and the graph must survive that. On ANY Web Audio failure
 * attach() returns the raw stream unchanged — graceful degradation to
 * exactly today's behavior.
 *
 * Per-user settings ({volume: 0..400, compressor: bool}) persist in
 * localStorage under a tenant-scoped key; entries at defaults are pruned.
 */

import { getSharedAudioContext, resumeAudioContext } from '../utils/audioContext';

const DEFAULT_SETTINGS = { volume: 100, compressor: false };

// Light "even them out" compressor (no makeup gain — boosting is the slider's
// job) and a conservative safety limiter: upstream gain can add +12 dB, so the
// ceiling sits at -3 dB with a hard ratio.
const COMPRESSOR_ON = { threshold: -24, knee: 12, ratio: 3, attack: 0.005, release: 0.15 };
const COMPRESSOR_OFF = { threshold: 0, knee: 0, ratio: 1, attack: 0.003, release: 0.25 };
const LIMITER = { threshold: -3, knee: 0, ratio: 20, attack: 0.001, release: 0.1 };

const RAMP = 0.03; // s — param ramp time constant

const clampVolume = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 100;
  return Math.min(400, Math.max(0, Math.round(n)));
};

class RemoteAudioManager {
  constructor() {
    this.ctx = null;
    this.storageKey = null; // set once tenantInfo resolves
    this._settings = null; // lazy-loaded map: userId -> {volume, compressor}
    this._users = new Map(); // userId -> rec (see _buildGraph)
    this._container = null; // hidden holder for the keep-alive <audio> elements
  }

  setStorageKey(key) {
    if (key === this.storageKey) return;
    this.storageKey = key;
    this._settings = null; // re-load lazily under the new key
  }

  // ---- settings persistence ------------------------------------------------

  _loadSettings() {
    if (this._settings) return this._settings;
    this._settings = {};
    if (this.storageKey) {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (raw) this._settings = JSON.parse(raw) || {};
      } catch (e) {
        /* corrupted/private mode — start fresh */
      }
    }
    return this._settings;
  }

  _saveSettings() {
    if (!this.storageKey || !this._settings) return;
    try {
      // Prune entries at defaults so the map doesn't grow unboundedly.
      const pruned = {};
      for (const [uid, s] of Object.entries(this._settings)) {
        if (!s) continue;
        if (clampVolume(s.volume) !== DEFAULT_SETTINGS.volume || !!s.compressor) {
          pruned[uid] = { volume: clampVolume(s.volume), compressor: !!s.compressor };
        }
      }
      this._settings = pruned;
      if (Object.keys(pruned).length === 0) localStorage.removeItem(this.storageKey);
      else localStorage.setItem(this.storageKey, JSON.stringify(pruned));
    } catch (e) {
      /* private mode / quota */
    }
  }

  getSettings(userId) {
    const s = this._loadSettings()[userId];
    return s
      ? { volume: clampVolume(s.volume), compressor: !!s.compressor }
      : { ...DEFAULT_SETTINGS };
  }

  // ---- attach / detach -----------------------------------------------------

  /**
   * Route `stream`'s audio through this user's listening chain. Returns the
   * PLAYBACK stream to bind to the tile's <video> element. Idempotent for the
   * same (userId, stream); a new stream replaces the old graph. Returns the
   * raw stream unchanged when Web Audio is unavailable or anything fails.
   */
  attach(userId, stream) {
    if (!stream) return stream;
    const existing = this._users.get(userId);
    if (existing && existing.srcStream === stream) return existing.playbackStream;
    if (existing) this.detach(userId);

    const ctx = getSharedAudioContext();
    if (!ctx) return stream; // no Web Audio — feature off
    this.ctx = ctx;
    resumeAudioContext();

    try {
      const rec = {
        srcStream: stream,
        playbackStream: new MediaStream(stream.getVideoTracks()),
        nodes: null,
        keepAliveEl: null,
        audioTrack: null,
        onAddTrack: null,
        onEnded: null,
      };

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        this._buildGraph(userId, rec, audioTrack);
      }
      // Late audio (a track added to the consumed stream after attach — e.g.
      // the publisher enables a mic mid-watch once the consume side supports
      // it). The element picks the processed track up live via addTrack.
      rec.onAddTrack = (e) => {
        if (e.track && e.track.kind === 'audio' && !rec.audioTrack) {
          try {
            this._buildGraph(userId, rec, e.track);
          } catch (err) {
            /* keep playing video-only */
          }
        }
      };
      stream.addEventListener('addtrack', rec.onAddTrack);

      this._users.set(userId, rec);
      return rec.playbackStream;
    } catch (e) {
      // Anything unexpected: play the raw stream, exactly as before.
      return stream;
    }
  }

  _buildGraph(userId, rec, audioTrack) {
    const ctx = this.ctx;
    const rawOnly = new MediaStream([audioTrack]);

    // Keep-alive element (muted — the audible path is the processed track in
    // the tile's <video>).
    const el = document.createElement('audio');
    el.muted = true;
    el.autoplay = true;
    el.setAttribute('aria-hidden', 'true');
    el.srcObject = rawOnly;
    this._getContainer().appendChild(el);

    const source = ctx.createMediaStreamSource(rawOnly);
    const gain = ctx.createGain();
    const compressor = ctx.createDynamicsCompressor();
    const limiter = ctx.createDynamicsCompressor();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    const destination = ctx.createMediaStreamDestination();

    source.connect(gain);
    gain.connect(compressor);
    compressor.connect(limiter);
    limiter.connect(analyser);
    analyser.connect(destination);

    rec.nodes = { source, gain, compressor, limiter, analyser, destination };
    rec.levelBuf = new Float32Array(analyser.fftSize);
    rec.keepAliveEl = el;
    rec.audioTrack = audioTrack;

    // Initial params — snap, no ramp (nothing audible yet). A graph built
    // mid-duck (self-listen test in progress) starts silent like the others.
    const s = this.getSettings(userId);
    gain.gain.value = this._ducked ? 0 : s.volume / 100;
    this._applyCompressor(rec, s.compressor, true);
    this._applyLimiter(rec);

    // Splice the processed track into the playback stream; a bound <video>
    // element picks the addition up live.
    const processed = destination.stream.getAudioTracks()[0];
    if (processed) rec.playbackStream.addTrack(processed);

    // The consumer closed (publisher left / unwatched elsewhere) — tear the
    // graph down; detach() handles the rest when App gets the event.
    rec.onEnded = () => this._teardownGraph(rec);
    audioTrack.addEventListener('ended', rec.onEnded);
  }

  _getContainer() {
    if (!this._container) {
      this._container = document.createElement('div');
      this._container.style.display = 'none';
      this._container.setAttribute('data-remote-audio-keepalive', '');
      document.body.appendChild(this._container);
    }
    return this._container;
  }

  _setParam(param, value) {
    const t = this.ctx ? this.ctx.currentTime : 0;
    try {
      param.cancelScheduledValues(t);
      param.setTargetAtTime(value, t, RAMP);
    } catch (e) {
      try {
        param.value = value;
      } catch (e2) {
        /* noop */
      }
    }
  }

  _applyCompressor(rec, on, snap = false) {
    const p = on ? COMPRESSOR_ON : COMPRESSOR_OFF;
    const c = rec.nodes.compressor;
    if (snap) {
      c.threshold.value = p.threshold;
      c.knee.value = p.knee;
      c.ratio.value = p.ratio;
      c.attack.value = p.attack;
      c.release.value = p.release;
      return;
    }
    this._setParam(c.threshold, p.threshold);
    this._setParam(c.knee, p.knee);
    this._setParam(c.ratio, p.ratio);
    this._setParam(c.attack, p.attack);
    this._setParam(c.release, p.release);
  }

  _applyLimiter(rec) {
    const l = rec.nodes.limiter;
    l.threshold.value = LIMITER.threshold;
    l.knee.value = LIMITER.knee;
    l.ratio.value = LIMITER.ratio;
    l.attack.value = LIMITER.attack;
    l.release.value = LIMITER.release;
  }

  // ---- user controls ---------------------------------------------------------

  setVolume(userId, pct) {
    const volume = clampVolume(pct);
    const map = this._loadSettings();
    map[userId] = { ...this.getSettings(userId), volume };
    this._saveSettings();
    const rec = this._users.get(userId);
    if (rec && rec.nodes) this._setParam(rec.nodes.gain.gain, volume / 100);
  }

  setCompressor(userId, on) {
    const map = this._loadSettings();
    map[userId] = { ...this.getSettings(userId), compressor: !!on };
    this._saveSettings();
    const rec = this._users.get(userId);
    if (rec && rec.nodes) this._applyCompressor(rec, !!on);
  }

  /**
   * Duck (silence) every remote user's audio while the local user listens to
   * their own mic test, and restore each to their saved volume after. All
   * remote audio flows through these graphs, so this covers everything.
   */
  duck(on) {
    this._ducked = !!on;
    for (const [userId, rec] of this._users) {
      if (!rec.nodes) continue;
      this._setParam(rec.nodes.gain.gain, on ? 0 : this.getSettings(userId).volume / 100);
    }
  }

  /** Whether this user currently has a live listening graph (audio present). */
  isAttached(userId) {
    const rec = this._users.get(userId);
    return !!(rec && rec.nodes);
  }

  /** Post-chain level {rms, peak} for the speaking meter. Zeros when absent. */
  getLevel(userId) {
    const rec = this._users.get(userId);
    if (!rec || !rec.nodes || !rec.levelBuf) return { rms: 0, peak: 0 };
    try {
      rec.nodes.analyser.getFloatTimeDomainData(rec.levelBuf);
    } catch (e) {
      return { rms: 0, peak: 0 };
    }
    const buf = rec.levelBuf;
    let sum = 0;
    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const s = buf[i];
      sum += s * s;
      const a = s < 0 ? -s : s;
      if (a > peak) peak = a;
    }
    return { rms: Math.sqrt(sum / buf.length), peak };
  }

  // ---- teardown --------------------------------------------------------------

  _teardownGraph(rec) {
    if (rec.audioTrack && rec.onEnded) {
      rec.audioTrack.removeEventListener('ended', rec.onEnded);
      rec.onEnded = null;
    }
    if (rec.nodes) {
      Object.values(rec.nodes).forEach((n) => {
        if (n && n.disconnect) {
          try {
            n.disconnect();
          } catch (e) {
            /* noop */
          }
        }
      });
      // Remove the (now-dead) processed track from the playback stream.
      const processed = rec.nodes.destination.stream.getAudioTracks()[0];
      if (processed) {
        try {
          rec.playbackStream.removeTrack(processed);
        } catch (e) {
          /* noop */
        }
      }
      rec.nodes = null;
    }
    if (rec.keepAliveEl) {
      try {
        rec.keepAliveEl.srcObject = null;
        rec.keepAliveEl.remove();
      } catch (e) {
        /* noop */
      }
      rec.keepAliveEl = null;
    }
    rec.audioTrack = null;
    // NOTE: never stop the consumed tracks — mediaService owns the consumers.
  }

  /**
   * Tear down only the audio graph, keeping the record (and its late-audio
   * addtrack listener) alive — for when the publisher's AUDIO producer closes
   * but their video tile stays up. Needed explicitly because a locally-closed
   * consumer stops its track without firing 'ended'.
   */
  detachAudio(userId) {
    const rec = this._users.get(userId);
    if (rec) this._teardownGraph(rec);
  }

  detach(userId) {
    const rec = this._users.get(userId);
    if (!rec) return;
    if (rec.onAddTrack) rec.srcStream.removeEventListener('addtrack', rec.onAddTrack);
    this._teardownGraph(rec);
    this._users.delete(userId);
  }

  disposeAll() {
    for (const userId of Array.from(this._users.keys())) this.detach(userId);
  }
}

const remoteAudio = new RemoteAudioManager();
export default remoteAudio;

// Dev-only debug handle (mirrors window.__mediaService / __voiceChain).
if (process.env.NODE_ENV !== 'production') {
  window.__remoteAudio = remoteAudio;
}
